import type { CalendarEvent } from "../../shared/types.js";

export type EventGroup = { dateKey: string; label: string; events: CalendarEvent[] };

const HONOLULU_TZ = "Pacific/Honolulu";

const dayKeyFmt = new Intl.DateTimeFormat("en-CA", {
  timeZone: HONOLULU_TZ,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

const dayLabelFmt = new Intl.DateTimeFormat("en-US", {
  timeZone: HONOLULU_TZ,
  weekday: "short",
  month: "short",
  day: "numeric",
});

const timeFmt = new Intl.DateTimeFormat("en-US", {
  timeZone: HONOLULU_TZ,
  hour: "numeric",
  minute: "2-digit",
});

export function dayKey(ev: CalendarEvent): string {
  if (ev.allDay) return ev.start.slice(0, 10);
  return dayKeyFmt.format(new Date(ev.start));
}

export function dayLabel(key: string): string {
  // key is YYYY-MM-DD; build a noon UTC date so timezone shifts don't move the day
  const d = new Date(`${key}T12:00:00Z`);
  return dayLabelFmt.format(d);
}

export function timeRange(ev: CalendarEvent): string {
  if (ev.allDay) return "All day";
  const start = timeFmt.format(new Date(ev.start));
  if (!ev.end) return start;
  const end = timeFmt.format(new Date(ev.end));
  return `${start} – ${end}`;
}

export function groupByDay(events: CalendarEvent[]): EventGroup[] {
  const map = new Map<string, CalendarEvent[]>();
  for (const ev of events) {
    const k = dayKey(ev);
    let bucket = map.get(k);
    if (!bucket) {
      bucket = [];
      map.set(k, bucket);
    }
    bucket.push(ev);
  }
  return Array.from(map.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([dateKey, evs]) => ({ dateKey, label: dayLabel(dateKey), events: evs }));
}

export function relativeDayLabel(key: string): string | null {
  const today = dayKeyFmt.format(new Date());
  const tomorrow = dayKeyFmt.format(new Date(Date.now() + 24 * 60 * 60 * 1000));
  if (key === today) return "Today";
  if (key === tomorrow) return "Tomorrow";
  return null;
}

export function todayHawaiiKey(): string {
  return dayKeyFmt.format(new Date());
}

/** Add `days` (can be negative) to a YYYY-MM-DD key, returning a new YYYY-MM-DD key. */
export function shiftDayKey(key: string, days: number): string {
  const d = new Date(`${key}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return dayKeyFmt.format(d);
}
